import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "card sensor-status-card" }
const _hoisted_2 = { class: "card-hdr" }
const _hoisted_3 = { class: "title-group" }
const _hoisted_4 = { class: "live-status" }
const _hoisted_5 = {
  key: 0,
  class: "overall-ts",
  title: "Ultimo valore disponibile"
}
const _hoisted_6 = { class: "hdr-actions" }
const _hoisted_7 = { class: "search-overlay" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "card-body scrollable" }
const _hoisted_10 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_11 = {
  key: 1,
  class: "empty-state"
}
const _hoisted_12 = {
  key: 2,
  class: "sensor-list"
}
const _hoisted_13 = { class: "sensor-main" }
const _hoisted_14 = ["title"]
const _hoisted_15 = { class: "sensor-value" }
const _hoisted_16 = {
  key: 0,
  class: "sensor-unit"
}
const _hoisted_17 = { class: "sensor-meta" }
const _hoisted_18 = { class: "sensor-id" }

import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import axios from "@/axiosInstance";
import { SENSOR_LABELS, SENSOR_ORDER } from "@/config/chartConfig";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faSync, faSearch } from "@fortawesome/free-solid-svg-icons";


export default /*@__PURE__*/_defineComponent({
  __name: 'SensorStatusCard',
  props: {
    deviceId: {}
  },
  setup(__props: any) {

const props = __props;

const sensors = ref<any[]>([]);
const loading = ref(false);
const lastUpdate = ref<Date | null>(null);
const isDelay = ref(true);
const searchQuery = ref("");
const showSearch = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);
let refreshInterval: any = null;

const formattedOverallTs = computed(() => {
  if (!lastUpdate.value) return "";
  const d = lastUpdate.value;
  
  // Format: YYYY-MM-DD HH:mm:ss (UTC to match charts)
  const pad = (n: number) => String(n).padStart(2, '0');
  const Y = d.getUTCFullYear();
  const M = pad(d.getUTCMonth() + 1);
  const D = pad(d.getUTCDate());
  const h = pad(d.getUTCHours());
  const m = pad(d.getUTCMinutes());
  const s = pad(d.getUTCSeconds());
  
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
});

const filteredSensors = computed(() => {
  let list = sensors.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(s => 
      s.label.toLowerCase().includes(q) || 
      s.id.toLowerCase().includes(q)
    );
  }
  return list;
});

async function fetchLatestData() {
  if (!props.deviceId) return;
  loading.value = true;
  try {
    const response = await axios.get(`/records/devices/${props.deviceId}/measurements`, {
      params: {
        range: "24h",
        limit: 1000,
        sort: "desc"
      }
    });

    const data = response.data || [];
    processData(data);
  } catch (err) {
    console.error("[SensorStatusCard] Error fetching data:", err);
  } finally {
    loading.value = false;
  }
}

function processData(data: any[]) {
  const latestByEntity: Record<string, any> = {};
  let overallLastTs: number = 0;

  data.forEach((item) => {
    // Only include sensors with a label
    if (SENSOR_LABELS[item.entity_id]) {
      // Since data is now sorted DESC (newest first), the first occurrence of an entity is the latest one
      if (!latestByEntity[item.entity_id]) {
        const ts = new Date(item.ts).getTime();
        latestByEntity[item.entity_id] = {
          id: item.entity_id,
          label: SENSOR_LABELS[item.entity_id],
          value: item.value_num !== null ? item.value_num : item.value,
          ts: ts,
          formattedTs: new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
        };
        if (ts > overallLastTs) overallLastTs = ts;
      }
    }
  });

  // sensors.value = Object.values(latestByEntity).sort((a, b) => a.label.localeCompare(b.label));

  const orderIndex = new Map(SENSOR_ORDER.map((id, idx) => [id, idx]));

sensors.value = Object.values(latestByEntity).sort((a: any, b: any) => {
  const ai = orderIndex.has(a.id) ? orderIndex.get(a.id)! : Number.POSITIVE_INFINITY;
  const bi = orderIndex.has(b.id) ? orderIndex.get(b.id)! : Number.POSITIVE_INFINITY;

  // 1) prima quelli nella lista, rispettando l'ordine
  if (ai !== bi) return ai - bi;

  // 2) poi tutti gli altri: alfabetico per label (come facevi già)
  return String(a.label).localeCompare(String(b.label), "it", { sensitivity: "base" });
});

  
  if (overallLastTs > 0) {
    lastUpdate.value = new Date(overallLastTs);
    const now = Date.now();
    const threshold = 15 * 60 * 1000;
    isDelay.value = (now - overallLastTs) > threshold;
  }
}

function toggleSearch() {
  showSearch.value = !showSearch.value;
  if (showSearch.value) {
    nextTick(() => searchInput.value?.focus());
  } else {
    searchQuery.value = "";
  }
}

function onSearchBlur() {
  if (!searchQuery.value) showSearch.value = false;
}

function startPolling() {
  stopPolling();
  refreshInterval = setInterval(fetchLatestData, 120000);
}

function stopPolling() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

watch(() => props.deviceId, () => {
  fetchLatestData();
});

onMounted(() => {
  fetchLatestData();
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
});
function getLabelWithoutUnit(label: string): string {
  if (!label) return "";
  return label.replace(/\s*\([^)]*\)$/, "").trim();
}

function getUnit(label: string): string {
  if (!label) return "";
  const match = label.match(/\(([^)]+)\)$/);
  return match ? match[1] : "";
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("h3", null, "Sensor status", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("span", {
            class: _normalizeClass(["live-indicator", { 'is-delay': isDelay.value }])
          }, [
            _cache[1] || (_cache[1] = _createElementVNode("span", { class: "dot" }, null, -1)),
            _createTextVNode(" " + _toDisplayString(isDelay.value ? "DELAY" : "LIVE"), 1)
          ], 2),
          (formattedOverallTs.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(formattedOverallTs.value), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _withDirectives(_createElementVNode("input", {
            ref_key: "searchInput",
            ref: searchInput,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
            type: "text",
            placeholder: "Cerca sensore...",
            onBlur: onSearchBlur
          }, null, 544), [
            [_vModelText, searchQuery.value]
          ])
        ]),
        _createElementVNode("button", {
          class: "ico-btn-card",
          onClick: fetchLatestData,
          disabled: loading.value,
          title: "Aggiorna"
        }, [
          _createVNode(_unref(FontAwesomeIcon), {
            icon: _unref(faSync),
            spin: loading.value
          }, null, 8, ["icon", "spin"])
        ], 8, _hoisted_8)
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      (loading.value && !sensors.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(_unref(FontAwesomeIcon), {
              icon: _unref(faSync),
              spin: ""
            }, null, 8, ["icon"])
          ]))
        : (!filteredSensors.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(searchQuery.value ? 'Nessun match' : 'Nessun dato'), 1))
          : (_openBlock(), _createElementBlock("ul", _hoisted_12, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSensors.value, (sensor) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: sensor.id,
                  class: "sensor-item"
                }, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("span", {
                      class: "sensor-label",
                      title: sensor.id
                    }, _toDisplayString(getLabelWithoutUnit(sensor.label)), 9, _hoisted_14),
                    _createElementVNode("span", _hoisted_15, [
                      _createTextVNode(_toDisplayString(sensor.value), 1),
                      (getUnit(sensor.label))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(getUnit(sensor.label)), 1))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("span", _hoisted_18, _toDisplayString(sensor.id), 1)
                  ])
                ]))
              }), 128))
            ]))
    ])
  ]))
}
}

})